/**
 * /security/verify: the second step of signing in for staff and clinic members, with a code from
 * their authenticator app, a backup code, or a passkey on this device. When the sign-in has ended,
 * or five wrong codes ended it, the page links back to the sign-in page the person came through.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import { Button } from "@lattice/ui/Button";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import type { SecurityStatus } from "../api.ts";
import { loadSecurity, loadSession, signOut, verifySecondFactor } from "../api.ts";
import { SUPPORT_ADDRESS } from "../PublicPage.tsx";
import { AuthLayout, authStyles, nextPath, signInAgainPath, TryAgain } from "../AuthLayout.tsx";
import { signInWithPasskey } from "../passkey.ts";
import { CodeForm } from "./CodeForm.tsx";
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "signed_out";
  readonly replacedForm: boolean;
} | {
  readonly kind: "too_many_wrong_codes";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "verify";
  readonly status: SecurityStatus;
};
function leave(): void {
  window.location.assign(nextPath() ?? "/");
}
function setupInstead(): void {
  const next = nextPath();
  window.location.assign(next === null ? "/security/setup" : `/security/setup?next=${encodeURIComponent(next)}`);
}

/** A screen that ends the second step, with the way back to signing in. */
function Ended({
  title,
  lead,
  replacedForm
}: {
  readonly title: string;
  readonly lead: string;
  readonly replacedForm: boolean;
}) {
  useEffect(() => {
    document.title = `${title} · Lattice`;
  }, [title]);
  return <AuthLayout title={title} lead={lead} focusHeading={replacedForm}>
      <LinkButton href={signInAgainPath()}>Sign in again</LinkButton>
    </AuthLayout>;
}
async function begin(): Promise<Screen> {
  const status = await loadSecurity();
  if (status === "signed_out") return {
    kind: "signed_out",
    replacedForm: false
  };
  if (status === null) return {
    kind: "failed"
  };
  if (status.passed) leave();else if (!status.totp && status.passkeys === 0) setupInstead();else return {
    kind: "verify",
    status
  };
  return {
    kind: "loading"
  };
}
export function SecurityVerify() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const [passkeyProblem, setPasskeyProblem] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [leaveProblem, setLeaveProblem] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  useEffect(() => {
    document.title = "Confirm it's you · Lattice";
    void begin().then(setScreen, () => setScreen({
      kind: "failed"
    }));
    /* Names the account the code is for, so someone on the wrong one knows to sign out. */
    void loadSession().then(session => setAccount(session.status === "ok" ? session.me.email : null));
  }, []);
  if (screen.kind === "loading") return <AuthLayout title="Confirm it's you" lead="Checking your sign-in.">
        <Pending message="Checking" bones={["line", "control"]} />
      </AuthLayout>;
  if (screen.kind === "signed_out") return <Ended title="Your sign-in has ended" lead="Sign in again with your email." replacedForm={screen.replacedForm} />;

  /*
    Five wrong codes end this session, and ten for one person in 15 minutes refuse codes from every
    session. The Worker answers both with the same refusal, so the copy has to hold for either.
  */
  if (screen.kind === "too_many_wrong_codes") return <Ended title="Too many wrong codes" lead="Sign in again with your email. If you've entered ten wrong codes in the last 15 minutes, wait 15 minutes first." replacedForm />;
  if (screen.kind === "failed") return <AuthLayout title="We couldn't check your sign-in" lead="Try again in a minute. If it keeps happening, write to support." focusHeading>
        <TryAgain />
      </AuthLayout>;
  const leaveNow = async () => {
    setLeaving(true);
    setLeaveProblem(null);
    try {
      await signOut();
      window.location.assign("/");
    } catch {
      setLeaving(false);
      setLeaveProblem("We couldn't sign you out. Check your connection and try again.");
    }
  };
  const usePasskey = async () => {
    setPasskeyProblem(null);
    const outcome = await signInWithPasskey();
    if (outcome === "ok") leave();else if (outcome !== "cancelled") setPasskeyProblem("That passkey didn't work. Use a code from your app instead.");
  };
  return <AuthLayout title="Confirm it's you" lead={screen.status.totp ? "Enter the code from your authenticator app, or one of your backup codes." : "Use the passkey on this device."}>
      {screen.status.totp ? <CodeForm submitLabel="Continue" check={verifySecondFactor} onPassed={leave} onEnded={outcome => setScreen(outcome === "signed_out" ? {
      kind: "signed_out",
      replacedForm: true
    } : {
      kind: "too_many_wrong_codes"
    })} acceptsBackupCodes focusOnMount /> : null}
      {screen.status.passkeys > 0 ? <Button tone={screen.status.totp ? "quiet" : "solid"} onClick={() => void usePasskey()}>
          Use a passkey
        </Button> : null}
      {passkeyProblem === null ? null : <p role="alert" {...stylex.props(authStyles.error)}>
          {passkeyProblem}
        </p>}
      <div {...stylex.props(authStyles.form)}>
        <p {...stylex.props(authStyles.body)} data-testid="lost-second-step">
          Lost your app and your backup codes? Email{" "}
          <a href={`mailto:${SUPPORT_ADDRESS}`}>{SUPPORT_ADDRESS}</a> from your account address. We
          check it is you, then reset your second step.
        </p>
        {account === null ? null : <p {...stylex.props(authStyles.body)} data-testid="signing-in-as">
            Signing in as {account}.
          </p>}
        <Button tone="quiet" size="md" testId="not-you" busy={leaving} onClick={() => void leaveNow()}>
          Sign out
        </Button>
        {leaveProblem === null ? null : <p role="alert" {...stylex.props(authStyles.error)}>
            {leaveProblem}
          </p>}
      </div>
    </AuthLayout>;
}